/**
 * Feature Enablement: who the table is about, what each feature says for
 * them, and one Save for the lot.
 *
 * The table names the thing it is about — Feature, Category, Source, Value —
 * rather than repeating "Name" and "Setting" on every screen. Source is read
 * only: it says which scope the value came from, and the subject bar is where
 * you go to edit a different one.
 *
 * Nothing is written until Save. Every toggle changes the draft, the bar
 * counts what has moved, and one request carries all of it.
 */

import type { CustomerFeatureListItem } from "@wallyo/contracts/console/configuration";
import type { FeatureSubject } from "@wallyo/contracts/console/configuration";

import { customerFeatureName, categoryName, t } from "../../i18n";
import {
  EVERY_SUBJECT_KIND,
  categoriesOf,
  isChanged,
  isOwnValue,
  notEditableLabel,
  shownValue,
  sourceLabel,
  type Draft,
  type FeatureFilters,
  type SubjectFields,
} from "./feature-save-model";

/** The initials on the subject pill: one number resolved into a person. */
function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/** The one thing on the row that says who: a person, a group, or neither. */
function SubjectPill(props: {
  fields: SubjectFields;
  resolved: { uuid: number; name: string; kind: "client" | "pro" } | null;
  missing: "none" | "unknown" | "staff";
}) {
  const { fields, resolved, missing } = props;
  if (missing !== "none") {
    return (
      <span className="subject-pill missing" role="status">
        <span className="subject-name">{t(missing === "staff" ? "features.pill.staff" : "features.pill.unknown")}</span>
      </span>
    );
  }
  if (resolved) {
    return (
      <span className="subject-pill">
        <span className="subject-initials" aria-hidden="true">
          {initialsOf(resolved.name)}
        </span>
        <span className="subject-name">{resolved.name}</span>
        <span className="subject-sep" aria-hidden="true" />
        <span className="subject-kind-tag">
          {t(resolved.kind === "pro" ? "features.kind.pro" : "features.kind.client")}
        </span>
      </span>
    );
  }
  // A group: no name and no face, but it still says who.
  return (
    <span className="subject-pill group">
      <span className="subject-name">
        {t(fields.kind === "allPros" ? "features.pill.allPros" : "features.pill.allClients")}
      </span>
    </span>
  );
}

export function SubjectBar(props: {
  fields: SubjectFields;
  resolved: { uuid: number; name: string; kind: "client" | "pro" } | null;
  /** A number nobody holds, or one an employee holds: two different answers. */
  missing: "none" | "unknown" | "staff";
  problem: string | null;
  searching: boolean;
  onChange: (next: Partial<SubjectFields>) => void;
  onSearch: () => void;
}) {
  const { fields, resolved, missing, problem, searching, onChange, onSearch } = props;
  const forUser = fields.kind === "user";
  return (
    <form
      className="card subject-bar"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSearch();
      }}
    >
      {/* Three exclusive choices, so radios rather than a menu: all of them
          are visible, and picking one is one click instead of two. A plain
          div, not a fieldset: a legend lays itself out on its own line and
          would drop the UUID box below the radios. */}
      <span className="subject-label" id="feature-subject-label">
        {t("features.subject.for")}
      </span>
      <span className="subject-kind" role="radiogroup" aria-labelledby="feature-subject-label">
        {EVERY_SUBJECT_KIND.map((kind) => (
          <label key={kind} className="subject-choice">
            <input
              type="radio"
              name="feature-subject"
              value={kind}
              checked={fields.kind === kind}
              onChange={() => onChange({ kind })}
            />
            <span>{t(`features.subject.${kind}` as const)}</span>
          </label>
        ))}
      </span>
      {/* Only a person has a UUID, so the box is dead until one is chosen. */}
      <label className="subject-uuid">
        <span className="subject-label">{t("features.subject.uuid")}</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={!forUser}
          value={fields.uuid}
          placeholder={forUser ? t("features.subject.uuidHint") : ""}
          onChange={(event) => onChange({ uuid: event.target.value })}
        />
      </label>
      <button type="submit" className="btn primary" disabled={searching}>
        {searching ? t("features.subject.searching") : t("features.subject.search")}
      </button>
      {/* Who the table is about, always. The right-hand end answers the
          left-hand end: a name, a group, or the fact that the number found
          nobody. An empty space there would just be a question unanswered. */}
      {problem ? <span className="subject-problem">{problem}</span> : null}
      <span className="subject-who">
        <SubjectPill fields={fields} resolved={resolved} missing={missing} />
      </span>
    </form>
  );
}

export type FeatureSurface = "app" | "console";

/**
 * The two surfaces a feature can govern. Not "staff" and "customers": a staff
 * member is a user too, and what differs is which product the feature is in.
 */
export function SurfaceTabs(props: {
  surface: FeatureSurface;
  appCount: number;
  consoleCount: number;
  onSurface: (next: FeatureSurface) => void;
}) {
  const { surface, appCount, consoleCount, onSurface } = props;
  const tabs: readonly { id: FeatureSurface; label: string; count: number }[] = [
    { id: "app", label: t("features.surface.app"), count: appCount },
    { id: "console", label: t("features.surface.console"), count: consoleCount },
  ];
  return (
    <div className="surface-tabs" role="tablist">
      {tabs.map(({ id, label, count }) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={surface === id}
          className={surface === id ? "surface-tab on" : "surface-tab"}
          onClick={() => onSurface(id)}
        >
          {label} <span className="surface-count">{count}</span>
        </button>
      ))}
    </div>
  );
}

export function FeatureFilterBar(props: {
  features: readonly CustomerFeatureListItem[];
  filters: FeatureFilters;
  onChange: (next: Partial<FeatureFilters>) => void;
}) {
  const { features, filters, onChange } = props;
  return (
    <div className="filter-chips">
      <label className="chip">
        <span>{t("features.col.category")}:</span>
        <select
          value={filters.category}
          onChange={(event) => onChange({ category: event.target.value as FeatureFilters["category"] })}
        >
          <option value="all">{t("features.filter.any")}</option>
          {categoriesOf(features).map((category) => (
            <option key={category} value={category}>
              {categoryName(category)}
            </option>
          ))}
        </select>
      </label>
      <label className="chip">
        <span>{t("features.col.value")}:</span>
        <select
          value={filters.value}
          onChange={(event) => onChange({ value: event.target.value as FeatureFilters["value"] })}
        >
          <option value="any">{t("features.filter.any")}</option>
          <option value="on">{t("features.value.on")}</option>
          <option value="off">{t("features.value.off")}</option>
        </select>
      </label>
      <label className="chip">
        <span>{t("features.col.source")}:</span>
        <select
          value={filters.source}
          onChange={(event) => onChange({ source: event.target.value as FeatureFilters["source"] })}
        >
          <option value="any">{t("features.filter.any")}</option>
          <option value="own">{t("features.filter.own")}</option>
          <option value="inherited">{t("features.filter.inherited")}</option>
          <option value="default">{t("features.source.default")}</option>
        </select>
      </label>
      <label className="chip chip-search">
        <span>{t("features.filter.search")}</span>
        <input type="search" value={filters.search} onChange={(event) => onChange({ search: event.target.value })} />
      </label>
    </div>
  );
}

function ValueCell(props: { feature: CustomerFeatureListItem; draft: Draft; onToggle: () => void }) {
  const { feature, draft, onToggle } = props;
  const on = shownValue(feature, draft);
  const locked = notEditableLabel(feature);
  const cleared = draft[feature.settingId] === null;
  return (
    <span className="value-cell">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={customerFeatureName(feature.settingId)}
        className={`toggle${on ? " on" : ""}${isChanged(feature, draft) ? " dirty" : ""}`}
        disabled={locked !== null}
        onClick={onToggle}
      >
        <span className="knob" />
      </button>
      {/* The stored value is a boolean; On and Off are the words for it. */}
      <span className="value-word">{t(on ? "features.value.on" : "features.value.off")}</span>
      {isChanged(feature, draft) && !cleared ? (
        <span className="value-note unsaved">{t("features.unsaved")}</span>
      ) : null}
      {cleared ? <span className="value-note cleared">{t("features.cleared")}</span> : null}
      {locked ? <span className="value-note locked">{locked}</span> : null}
    </span>
  );
}

export function FeatureTable(props: {
  features: readonly CustomerFeatureListItem[];
  subject: FeatureSubject;
  draft: Draft;
  onToggle: (feature: CustomerFeatureListItem) => void;
}) {
  const { features, subject, draft, onToggle } = props;
  return (
    <div className="card">
      <table className="config-list feature-table">
        <thead>
          <tr>
            <th>{t("features.col.feature")}</th>
            <th>{t("features.col.category")}</th>
            <th>{t("features.col.source")}</th>
            <th>{t("features.col.value")}</th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => {
            const own = isOwnValue(feature, subject);
            return (
              <tr key={feature.settingId} className={isChanged(feature, draft) ? "dirty" : undefined}>
                <td>
                  <a className="subject-link" href={`#features/${feature.settingId}`}>
                    {customerFeatureName(feature.settingId)}
                  </a>
                  {feature.status === "planned" ? <span className="pill quiet">{t("features.planned")}</span> : null}
                </td>
                <td>{categoryName(feature.category)}</td>
                {/* Read only. It says where the value came from; the subject bar
                    is where you go to edit a different scope. */}
                <td className="source-cell">
                  <span className={own ? "pill own" : "muted"}>{sourceLabel(feature.source, subject)}</span>
                </td>
                <td>
                  <ValueCell feature={feature} draft={draft} onToggle={() => onToggle(feature)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {features.length === 0 ? <div className="empty">{t("features.filter.none")}</div> : null}
    </div>
  );
}

/**
 * The Save bar: it appears only once something has moved, and it is the only
 * thing on the screen that writes. One reason covers the whole save, because
 * the server records one reason per transaction.
 */
/** One and many are different sentences, so they are different messages. */
export function countLabel(which: "title" | "submit" | "done", count: number): string {
  return count === 1 ? t(`features.save.${which}.one` as const) : t(`features.save.${which}.many` as const, { count });
}

export function SaveBar(props: {
  count: number;
  reason: string;
  pending: boolean;
  problem: string | null;
  onReason: (next: string) => void;
  onDiscard: () => void;
  onSave: () => void;
}) {
  const { count, reason, pending, problem, onReason, onDiscard, onSave } = props;
  if (count === 0) return null;
  return (
    <div className="save-bar" role="region" aria-label={countLabel("title", count)}>
      <span className="save-count">{countLabel("title", count)}</span>
      <label className="save-reason">
        <span>{t("team.reason")}</span>
        <input type="text" value={reason} onChange={(event) => onReason(event.target.value)} />
      </label>
      {problem ? <p className="field-problem">{problem}</p> : null}
      <div className="btns">
        <button type="button" className="btn quiet" onClick={onDiscard} disabled={pending}>
          {t("features.save.discard")}
        </button>
        <button type="button" className="btn primary" onClick={onSave} disabled={pending}>
          {pending ? t("features.save.sending") : countLabel("submit", count)}
        </button>
      </div>
    </div>
  );
}
